import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';

import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ListView from '../../SystemDesign/ListViewV2';
import ListViewMenuActions from '../../SystemDesign/ListViewMenuActions';
import DateInTable from '../../SystemDesign/DateInTable';
import SearchInput from '../../SystemDesign/SearchInput';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';
import MuiDialog from '../../SystemDesign/MuiDialog';
import { PrimaryButton, SubTitleScreen, TitleScreen } from '../../SystemDesign/Styles';
import { tokens } from '../../theme';
import { formattedDate, isEmpty } from '../../Utils/Functions';
import { useGetQuizAssignmentsQuery } from '../../services/SkillsManagement';
import UpdateQuizAssignment from './UpdateQuizAssignment';
import DeleteQuizAssignement from './DeleteQuizAssignement';
import CreateAssignement from './CreateAssignement';
import RevokeAssignment from './RevokeAssignment';

const StatusChip = ({ value }) => {
  const colorMap = {
    pending: '#ff9800',
    accepted: '#4caf50',
    revoked: '#9e9e9e',
    declined: '#f44336',
    expired: '#795548',
    cancelled: '#616161',
  };
  const bg = `${colorMap[value] || '#9e9e9e'}22`;
  const fg = colorMap[value] || '#616161';
  return (
    <span style={{ color: fg, background: bg, padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
      {value || 'pending'}
    </span>
  );
};

const QuizAssignementList = ({ showHeader = true, showAddButton = true, showSearch = true }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmOrLarger = useMediaQuery((t) => t.breakpoints.up('sm'));
  const isMdOrLarger = useMediaQuery((t) => t.breakpoints.up('md'));

  const [isOpen, setIsOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination & sorting state saved similarly to PostesList
  const [queryParams, setQueryParams] = useState(() => {
    const savedRowsPerPage = localStorage.getItem(`quiz-assignments-rowsPerPage`);
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    return {
      filters: '',
      sorting: '',
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage }),
    };
  });

  // Derive API args
  const { currentPage, rowsPerPage } = useMemo(() => {
    const p = JSON.parse(queryParams.pagination || '{}');
    return { currentPage: p.currentPage || 1, rowsPerPage: p.rowsPerPage || 10 };
  }, [queryParams]);

  // Hook call: this endpoint expects simple args
  const dispatch = useDispatch();
  const { data, isLoading, isFetching, error, refetch } = useGetQuizAssignmentsQuery(
    {
      page: currentPage,
      limit: rowsPerPage,
      // Add filters here when needed, e.g. status, quizId, etc.
    },
    { refetchOnMountOrArgChange: true }
  );

  // Map API pagination to ListViewV2 shape
  const paginationMapped = useMemo(() => {
    const p = data?.data?.pagination;
    if (!p) return { currentPage, rowsPerPage, total: 0, totalPages: 0 };
    return {
      currentPage: p.page || currentPage,
      rowsPerPage: p.limit || rowsPerPage,
      total: p.total || 0,
      totalPages: p.totalPages || Math.ceil((p.total || 0) / (p.limit || rowsPerPage)),
    };
  }, [data, currentPage, rowsPerPage]);

  // Table columns (human-readable)
  const columns = useMemo(
    () => [
      {
        id: 'quizTitle',
        header: 'Quiz',
        accessorKey: 'quizTitle',
        cell: ({ row }) => row?.original?.quiz?.title || `Quiz #${row?.original?.quiz?.quizId ?? row?.original?.quizId ?? ''}`,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'assigneeUsername',
        header: 'Assignee',
        accessorKey: 'assigneeUsername',
        cell: ({ row }) => row?.original?.assignee?.username || row?.original?.assignee?.email || `User #${row?.original?.assigneeUserId ?? ''}`,
        enableColumnFilter: false,
        enableSorting: false,
      },
      ...(isSmOrLarger
        ? [
            {
              id: 'assignerUsername',
              header: 'Assigned by',
              accessorKey: 'assignerUsername',
              cell: ({ row }) => row?.original?.assigner?.username || row?.original?.assigner?.email || `User #${row?.original?.assignedByUserId ?? ''}`,
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <StatusChip value={info.getValue()} />,
        enableColumnFilter: false,
        enableSorting: false,
      },
      ...(isMdOrLarger
        ? [
            {
              id: 'dueAt',
              accessorKey: 'dueAt',
              header: 'Due at',
              cell: (info) =>
                info.getValue() ? <DateInTable date={formattedDate(new Date(info.getValue()))} /> : '—',
              enableColumnFilter: false,
              enableSorting: false,
            },
            {
              id: 'expiresAt',
              accessorKey: 'expiresAt',
              header: 'Expires at',
              cell: (info) =>
                info.getValue() ? <DateInTable date={formattedDate(new Date(info.getValue()))} /> : '—',
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      ...(isMdOrLarger
        ? [
            {
              id: 'createdAt',
              accessorKey: 'createdAt',
              header: 'Created at',
              cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))} />,
              enableColumnFilter: false,
              enableSorting: false,
            },
            {
              id: 'updatedAt',
              accessorKey: 'updatedAt',
              header: 'Updated at',
              cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))} />,
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      {
        id: 'actions',
        header: '',
        size: 100,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const status = row?.original?.status;
          const isPending = status === 'pending';
          if (!isPending) return null;
          return (
            <ListViewMenuActions>
              <UpdateQuizAssignment row={row} actionKey="update" />
              <RevokeAssignment row={row} actionKey="revoke" />
              <DeleteQuizAssignement row={row} actionKey="delete" />
            </ListViewMenuActions>
          );
        },
      },
    ],
    [isSmOrLarger, isMdOrLarger]
  );

  // Filters/pagination updater (mirrors PostesList.jsx)
  const applyFilters = (updatedFilters = columnFilters, paginationInfo = null) => {
    const filtersArray = updatedFilters || [];
    const formattedFilters = filtersArray.reduce((acc, filter) => {
      if (typeof filter === 'object' && !Array.isArray(filter)) {
        const [key, values] = Object.entries(filter)[0];
        if (Array.isArray(values)) {
          const nonEmpty = values.filter((v) => v !== '');
          if (nonEmpty.length > 0) acc[key] = nonEmpty.map((v) => String(v));
        } else if (values !== '') {
          acc[key] = [String(values)];
        }
      }
      return acc;
    }, {});

    // Rows per page management
    let nextRows = paginationInfo?.rowsPerPage;
    if (!nextRows) {
      const saved = localStorage.getItem(`quiz-assignments-rowsPerPage`);
      nextRows = saved !== null ? parseInt(saved, 10) : rowsPerPage || 10;
    } else {
      localStorage.setItem(`quiz-assignments-rowsPerPage`, String(nextRows));
    }

    setQueryParams((prev) => ({
      ...prev,
      filters: isEmpty(formattedFilters) ? '' : JSON.stringify(formattedFilters),
      pagination: JSON.stringify({
        currentPage: paginationInfo?.currentPage || 1,
        rowsPerPage: nextRows,
      }),
      // sorting kept for future enhancement
      sorting: prev.sorting,
    }));
  };

  // Search input behavior: simple client-side parameter trigger only (extend later as API supports)
  const handleSearchChange = (val) => setSearchTerm(val);
  const handleSearchSubmit = () => {
    // Hook does not support free-text search yet; you can add mappings here when API supports.
    applyFilters(null, { currentPage: 1, rowsPerPage });
  };

  // Sync API data into Redux so local actions (revoke/update/delete) reflect immediately
  useEffect(() => {
    const apiRows = data?.data?.assignments;
    if (Array.isArray(apiRows)) {
      dispatch({ type: actions.CLEAR_QUIZ_ASSIGNMENTS });
      dispatch({ type: actions.INITIALIZE_QUIZ_ASSIGNMENTS, payload: apiRows });
    }
  }, [data, dispatch]);

  // Prefer Redux state for rendering so UI reflects local updates instantly
  const reduxRows = useSelector((state) => state.quizAssignments || []);
  const rows = (reduxRows && reduxRows.length > 0) ? reduxRows : (data?.data?.assignments || []);
  const rowsKey = useMemo(() => {
    if (!Array.isArray(rows)) return 'empty';
    // Build a lightweight signature from id and status so UI remounts when revoke/update happens
    return rows.map(r => `${r.assignmentId ?? r.id}:${r.status ?? ''}`).join('|');
  }, [rows]);

  // React to global changes (e.g. revoke) by refetching
  useEffect(() => {
    const handler = () => {
      try { refetch?.(); } catch {}
    };
    window.addEventListener('quiz-assignments:changed', handler);
    return () => window.removeEventListener('quiz-assignments:changed', handler);
  }, [refetch]);

  return (
    <Box p={3}>
      <div>
        {showHeader && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <div>
              <Typography sx={TitleScreen(colors)}>Quiz Assignments</Typography>
              <Typography sx={SubTitleScreen(colors)}>
                Manage quiz assignments and review their status.
              </Typography>
            </div>
            {showAddButton && (
              <div>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setIsOpen(true)}
                   variant="contained"
            color="primary"
                >
                  Add Assignment
                </Button>
              </div>
            )}
          </div>
        )}

        {showSearch && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '2rem' }}>
            <div style={{ flexGrow: 1 }}>
              <SearchInput
                placeholder="Search by quiz title or username..."
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearchSubmit}
              />
            </div>
          </div>
        )}

        {showAddButton && (
          <MuiDialog
            open={isOpen}
            onClose={() => setIsOpen(false)}
            icon={<AddIcon />}
            title="Create New Assignment"
            subtitle="Assign a quiz to a user"
            content={<CreateAssignement open={isOpen} onClose={() => setIsOpen(false)} />}
            maxWidth="sm"
          />
        )}

        {isLoading ? (
          <LoaderSpinner loading={isLoading || isFetching} />
        ) : (
          <ListView
            key={`qa-list-${rowsKey}`}
            dispatchData={rows}
            columns={columns}
            tableName={'quiz-assignments'}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            applyFilters={applyFilters}
            pagination={paginationMapped}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
            showColumnManager={false}
          />
        )}
      </div>
    </Box>
  );
};

export default QuizAssignementList;